import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GroupDevice,
  useListGroupDevicesQuery,
  useMarkDeviceSuccessfulMutation,
  useResetDevicesStatusMutation,
  useUnassignFailedDeviceMutation,
  useExtendExpirationDateMutation,
} from 'src/backend';
import { useNavigate } from 'react-router-dom';
import { SECURITY_LOG } from 'src/router';
import LoadingIconButton from 'src/components/LoadingIconButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import ScreenLockLandscapeOutlinedIcon from '@mui/icons-material/ScreenLockLandscapeOutlined';
import { Stack } from '@mui/system';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAlert } from 'src/components/AlertsProvider/hooks';
import { AlertType } from 'src/components/AlertsProvider/types';

dayjs.extend(utc);

type Params = {
  groupName: string;
  groupStatus: string | undefined;
  shouldRefresh: boolean;
  refreshGroup: () => void;
  refreshStatus: () => void;
};

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    onResetSelected: () => void;
    onResetAllFailed: () => void;
    onExtendDevicesExpiration: () => void;
    isResetingStatus: boolean;
  }
}

function CustomToolbar(props: {
  onResetSelected: () => void;
  onResetAllFailed: () => void;
  onExtendDevicesExpiration: () => void;
  isExtendingExpiration: boolean;
  isResetingStatus: boolean;
  groupStatus: string;
}) {
  const {
    onResetSelected,
    onResetAllFailed,
    onExtendDevicesExpiration,
    isExtendingExpiration,
    isResetingStatus,
    groupStatus,
  } = props;
  return (
    <Stack direction="row" sx={{ marginBottom: '15px' }}>
      <Button
        disabled={isResetingStatus || isExtendingExpiration}
        variant="outlined"
        style={{ marginTop: '15px', marginRight: '10px' }}
        onClick={() => onResetSelected()}
      >
        Reset devices
      </Button>
      <Button
        disabled={isResetingStatus || isExtendingExpiration}
        variant="outlined"
        style={{ marginTop: '15px', marginRight: '10px' }}
        onClick={() => onResetAllFailed()}
      >
        Reset failed devices
      </Button>
      {groupStatus === 'running' && (
        <Button
          disabled={isResetingStatus || isExtendingExpiration}
          variant="outlined"
          style={{ marginTop: '15px' }}
          onClick={() => onExtendDevicesExpiration()}
        >
          Extend devices expiration
        </Button>
      )}
    </Stack>
  );
}

declare module '@mui/x-data-grid' {
  interface FooterPropsOverrides {
    onConfirm: () => void;
    showConfirmButton: boolean;
    isResetingStatus: boolean;
  }
}

export function CustomFooter(props: {
  onConfirm: () => void;
  showConfirmButton: boolean;
  isResetingStatus: boolean;
}) {
  const { onConfirm, showConfirmButton, isResetingStatus } = props;
  return (
    <Stack
      visibility={showConfirmButton ? 'visible' : 'hidden'}
      direction="row"
      sx={{ marginTop: 'px' }}
    >
      <LoadingIconButton onClick={() => onConfirm()} loading={isResetingStatus}>
        <Tooltip title="Reset selected devices">
          <CheckCircleIcon />
        </Tooltip>
      </LoadingIconButton>
    </Stack>
  );
}

function MigrationGroupReadOnly({
  groupName,
  groupStatus,
  shouldRefresh,
  refreshGroup,
  refreshStatus,
}: Params) {
  function getRowId(row: GroupDevice) {
    return row.serialNumber;
  }
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const { showAlert } = useAlert();

  const { data, isLoading, isFetching, refetch } =
    useListGroupDevicesQuery(groupName);

  const unassignDevice = useUnassignFailedDeviceMutation();
  const markDeviceSuccessful = useMarkDeviceSuccessfulMutation();
  const resetDevicesStatus = useResetDevicesStatusMutation();
  const extendExpirationDate = useExtendExpirationDateMutation();

  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [isResetingStatus, setIsResetingStatus] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  const latestExpirationDate = useMemo(() => {
    if (!data?.data || data.data.length === 0) {
      return dayjs.utc();
    }

    const expirationDates = data.data
      .map((device) => device.expiresOn)
      .filter((date): date is string => date != null)
      .map((date) => dayjs.utc(date));

    if (expirationDates.length === 0) {
      return dayjs.utc();
    }

    return expirationDates.reduce((latest, current) =>
      current.isAfter(latest) ? current : latest,
    );
  }, [data?.data]);

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    latestExpirationDate.add(7, 'day'),
  );

  useEffect(() => {
    setSelectedDate(latestExpirationDate.add(7, 'day'));
  }, [latestExpirationDate]);

  const performUnassign = useCallback(
    (serialNumber: string) => {
      unassignDevice[0](serialNumber)
        .unwrap()
        .then(() => refreshGroup())
        .catch();
    },
    [refreshGroup, unassignDevice],
  );

  const markSuccessful = useCallback(
    (serialNumber: string) => {
      markDeviceSuccessful[0](serialNumber)
        .unwrap()
        .then((val) => {
          apiRef.current.updateRows([
            { serialNumber: val.serialNumber, status: val.status },
          ]);
        })
        .catch()
        .finally(() => refreshStatus());
    },
    [markDeviceSuccessful, apiRef, refreshStatus],
  );

  useEffect(() => {
    if (shouldRefresh) {
      refetch();
    }
  }, [refetch, shouldRefresh]);

  const onResetSelected = () => {
    setShowCheckboxes(!showCheckboxes);
  };

  const onResetAllFailed = useCallback(() => {
    const failedDevices = data?.data
      .filter((x) => x.status === 'failed')
      .map((x) => x.serialNumber);
    if (failedDevices?.length === 0) {
      return;
    }
    setIsResetingStatus(true);
    resetDevicesStatus[0](failedDevices)
      .unwrap()
      .then(() => refreshGroup())
      .finally(() => setIsResetingStatus(false))
      .catch();
  }, [data, resetDevicesStatus, refreshGroup, setIsResetingStatus]);

  const onExtendDevicesExpiration = useCallback(() => {
    setOpenDatePicker(true);
  }, []);

  const handleCloseDatePicker = () => {
    setOpenDatePicker(false);
  };

  const handleConfirmDate = () => {
    if (selectedDate) {
      const utcDateString = selectedDate.utc().format('YYYY-MM-DDTHH:mm:ss');
      extendExpirationDate[0]({
        groupName,
        extendedExpirationDate: utcDateString,
      })
        .unwrap()
        .then(() => {
          showAlert({
            type: AlertType.Info,
            text: `Devices expiration date extended to ${selectedDate
              .utc()
              .format('YYYY-MM-DD HH:mm:ss')} UTC`,
          });
          refreshGroup();
          setOpenDatePicker(false);
        })
        .catch();
    }
  };

  const onConfirmSelected = useCallback(() => {
    const selectedDevices = rowSelectionModel.map((x) => x.toString());
    if (selectedDevices.length === 0) {
      return;
    }
    setIsResetingStatus(true);
    resetDevicesStatus[0](selectedDevices)
      .unwrap()
      .then(() => refreshGroup())
      .finally(() => setIsResetingStatus(false))
      .catch();
  }, [
    rowSelectionModel,
    resetDevicesStatus,
    refreshGroup,
    setIsResetingStatus,
  ]);

  const deviceColumns: GridColDef<GroupDevice>[] = [
    {
      field: 'serialNumber',
      headerName: 'Serial',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
    },
    {
      field: 'expiresOn',
      headerName: 'Expiration Date [UTC]',
      flex: 1,
      renderCell: (params) => {
        if (!params.value) {
          return '-';
        }
        const expiresOn = dayjs.utc(params.value);
        const now = dayjs.utc();
        const isPast = expiresOn.isBefore(now);
        const backgroundColor = isPast ? '#ffcccc' : '#ccffcc';

        return (
          <div
            style={{
              backgroundColor,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              paddingLeft: '8px',
            }}
          >
            {expiresOn.format('MM-DD-YYYY HH:mm A')}
          </div>
        );
      },
    },
    {
      field: 'updateType',
      headerName: 'Update Type',
      flex: 1,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      renderCell: (params) =>
        params.row.status === 'failed' ? (
          <>
            <LoadingIconButton
              onClick={() => markSuccessful(params.row.serialNumber)}
              loading={
                markDeviceSuccessful[1].isLoading || unassignDevice[1].isLoading
              }
            >
              <Tooltip title="Mark as successful">
                <CheckCircleIcon />
              </Tooltip>
            </LoadingIconButton>
            <LoadingIconButton
              onClick={() => performUnassign(params.row.serialNumber)}
              loading={
                unassignDevice[1].isLoading || markDeviceSuccessful[1].isLoading
              }
            >
              <Tooltip title="Unassign">
                <ErrorOutlineOutlinedIcon />
              </Tooltip>
            </LoadingIconButton>
            <IconButton
              onClick={() =>
                navigate(SECURITY_LOG, {
                  state: { serial: params.row.serialNumber },
                })
              }
            >
              <Tooltip title="Device security log">
                <ScreenLockLandscapeOutlinedIcon />
              </Tooltip>
            </IconButton>
          </>
        ) : (
          <IconButton
            onClick={() =>
              navigate(SECURITY_LOG, {
                state: { serial: params.row.serialNumber },
              })
            }
          >
            <Tooltip title="Device security log">
              <ScreenLockLandscapeOutlinedIcon />
            </Tooltip>
          </IconButton>
        ),
    },
  ];

  return (
    <Grid item xs={6} sx={{ width: '100%', paddingTop: '20px' }}>
      <DataGrid
        apiRef={apiRef}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        slots={{
          toolbar: CustomToolbar,
          footer: CustomFooter,
        }}
        slotProps={{
          toolbar: {
            onResetSelected,
            onResetAllFailed,
            onExtendDevicesExpiration,
            isExtendingExpiration: extendExpirationDate[1].isLoading,
            isResetingStatus,
            groupStatus,
          },
          footer: {
            onConfirm: () => onConfirmSelected(),
            showConfirmButton: showCheckboxes,
            isResetingStatus,
          },
        }}
        checkboxSelection={showCheckboxes}
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
        }}
        rowSelectionModel={rowSelectionModel}
        getRowId={(row) => getRowId(row)}
        columns={deviceColumns}
        rows={data?.data ?? []}
        loading={isLoading || isFetching || isResetingStatus}
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
            display: 'none',
          },
          '& .MuiDataGrid-overlay': {
            height: '100%',
          },
          '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-main': {
            border: 1,
            borderColor: 'black',
          },
        }}
        pageSizeOptions={[10]}
        hideFooterSelectedRowCount
      />

      <Dialog open={openDatePicker} onClose={handleCloseDatePicker}>
        <DialogTitle>Extend Devices Expiration</DialogTitle>
        <DialogContent sx={{ minWidth: 350 }}>
          <Typography variant="body1" color="text.secondary">
            Note: Only &apos;failed&apos; and &apos;waitingfordevice&apos;
            devices
            <br />
            expiration date will be extended.
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Expiration Date [UTC]"
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              timezone="UTC"
              minDateTime={latestExpirationDate}
              disablePast
              sx={{ marginTop: 3, width: '100%' }}
              slotProps={{
                textField: {
                  size: 'medium',
                },
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDatePicker}
            disabled={extendExpirationDate[1].isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDate}
            variant="contained"
            disabled={extendExpirationDate[1].isLoading}
          >
            {extendExpirationDate[1].isLoading ? 'Extending...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default MigrationGroupReadOnly;
